import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { AdminNavEntryPoint, AdminNavEntryPointV2 } from '@retailer-platform/dashboard/routing'
import { campaignManagementRouter } from './routing/campaignManagementRouter'
import { campaignManagementAdminRouter } from './routing/campaignManagementAdminRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import ES_ES from './translations/es-ES.json'
import FR_FR from './translations/fr-FR.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import {
  campaignManagementAdminAccessControl,
  campaignManagementAdminCalendarPageAccessControl,
  campaignManagementAdminCreativesPageAccessControl,
  campaignManagementAdminMezcalPagesControl,
  campaignManagementAdminMultiRetailerPagesViewControl,
  campaignManagementAdminRedemptionUrls,
} from './access-control/campaignManagementAdminAccess.configuration'
import { Permission } from './access-control/permissions'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'campaign-management',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'campaign-management-dashboard-tool',
  },
  routes,
  translations: { EN_US, ES_ES, FR_FR },
  routers: {
    scoped: [campaignManagementRouter],
    admin: [campaignManagementAdminRouter],
  },
  navigationEntries: [],
  adminNavigationEntries: [
    {
      attachTo: AdminNavEntryPoint.CampaignManagement,
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-campaign-management-theme-page-list',
          labelId: 'campaignManagement.themePages.listPage.title',
          accessControl: campaignManagementAdminAccessControl,
          positionNavV2: 5,
          inToolPermissions: [
            {
              slug: Permission.CampaignManagementPageBuilderView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementPageBuilderView,
            },
            {
              slug: Permission.CampaignManagementPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementPageBuilderModify,
            },
          ],
        },
      ],
    },
    {
      attachTo: AdminNavEntryPoint.CampaignManagement,
      subNavItems: [
        {
          route: 'app-admin-campaign-management-calendar',
          labelId: 'campaignManagement.calendar.title',
          accessControl: campaignManagementAdminCalendarPageAccessControl,
        },
      ],
    },
    {
      attachTo: AdminNavEntryPoint.Merchandising,
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-campaign-management-creatives',
          labelId: 'campaignManagement.admin.creativeManager.nav.title',
          accessControl: campaignManagementAdminCreativesPageAccessControl,
        },
      ],
    },
    {
      attachTo: AdminNavEntryPoint.CampaignManagement,
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-campaign-management-redemption-urls-list',
          labelId: 'campaignManagement.admin.redemptionUrls.title',
          accessControl: campaignManagementAdminRedemptionUrls,
          positionNavV2: 6,
        },
      ],
    },
    {
      attachTo: AdminNavEntryPoint.LandingPageSystem,
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-campaign-management-multi-retailer-pages-list',
          labelId: 'campaignManagement.admin.multiRetailerPages.title',
          accessControl: campaignManagementAdminMultiRetailerPagesViewControl,
          positionNavV2: 3,
          inToolPermissions: [
            {
              slug: Permission.CampaignManagementPageBuilderView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementPageBuilderView,
            },
            {
              slug: Permission.CampaignManagementPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementPageBuilderModify,
            },
          ],
        },
      ],
    },
    {
      attachTo: AdminNavEntryPoint.LandingPageSystem,
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-campaign-management-mezcal-pages-list',
          labelId: 'campaignManagement.admin.mezcalPages.title',
          accessControl: campaignManagementAdminMezcalPagesControl,
          positionNavV2: 4,
          inToolPermissions: [
            {
              slug: Permission.CampaignManagementPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementPageBuilderModify,
            },
          ],
        },
      ],
    },
  ],
}

export default definition
